import { useRef, useEffect, useCallback } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const W = 1080;
const H = 1080;
const GOLD = "#a855f7";
const GREEN = "#22c55e";

export function ShareHeatCard({ hitters, date, onClose }) {
  const canvasRef = useRef(null);
  const top5 = (hitters || []).slice(0, 5);

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    await Promise.all([
      document.fonts.load("700 120px Caveat"),
      document.fonts.load("900 52px 'Barlow Condensed'"),
      document.fonts.load("700 84px 'JetBrains Mono'"),
    ]).catch(() => {});
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W / 2, 300, 80, W / 2, 300, 820);
    glow.addColorStop(0, "rgba(34,197,94,0.14)");
    glow.addColorStop(1, "rgba(34,197,94,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 10;
    ctx.strokeRect(28, 28, W - 56, H - 56);
    ctx.strokeStyle = "rgba(168,85,247,0.35)";
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 8]);
    ctx.strokeRect(52, 52, W - 104, H - 104);
    ctx.setLineDash([]);
    ctx.textAlign = "center";

    ctx.fillStyle = GOLD;
    ctx.font = "700 86px Caveat";
    ctx.fillText("The Pooh Sheet", W / 2, 160);

    ctx.fillStyle = "#e5e5e5";
    ctx.font = "900 52px 'Barlow Condensed'";
    ctx.fillText("TODAY'S HEAT BOARD", W / 2, 236);
    ctx.fillStyle = GREEN;
    ctx.font = "900 38px 'Barlow Condensed'";
    ctx.fillText(`TOP 5 LAUNCHERS — ${date || ""}`, W / 2, 288);

    const maxProb = Math.max(...top5.map((h) => h.hr_prob || 0), 1);
    top5.forEach((h, i) => {
      const y = 360 + i * 106;
      ctx.textAlign = "left";
      ctx.fillStyle = GOLD;
      ctx.font = "700 52px 'JetBrains Mono'";
      ctx.fillText(String(i + 1), 96, y + 16);
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 44px 'Barlow Condensed'";
      ctx.fillText(`${h.name.toUpperCase()}`, 160, y);
      ctx.fillStyle = "#9a9a9a";
      ctx.font = "600 28px 'Barlow Condensed'";
      ctx.fillText(`${h.team}  ·  vs ${h.pitcher || "TBD"}`, 160, y + 40);
      // heat bar
      const bw = 240 * ((h.hr_prob || 0) / maxProb);
      ctx.fillStyle = "rgba(34,197,94,0.25)";
      ctx.fillRect(700, y - 26, 240, 20);
      ctx.fillStyle = GREEN;
      ctx.fillRect(700, y - 26, bw, 20);
      ctx.textAlign = "right";
      ctx.fillStyle = GREEN;
      ctx.font = "700 44px 'JetBrains Mono'";
      ctx.fillText(`${h.hr_prob ?? "—"}%`, 984, y + 32);
      ctx.textAlign = "left";
      ctx.fillStyle = "#6a6a6a";
      ctx.font = "600 22px 'Barlow Condensed'";
      ctx.fillText("ADJ HR", 700, y + 32);
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(96, y + 58);
      ctx.lineTo(W - 96, y + 58);
      ctx.stroke();
    });

    ctx.textAlign = "center";
    ctx.fillStyle = GOLD;
    ctx.font = "700 60px Caveat";
    ctx.fillText("It's Not A Sin, If You WIN!", W / 2, 950);
    ctx.fillStyle = "#9a9a9a";
    ctx.font = "500 32px 'JetBrains Mono'";
    ctx.fillText("thepoohsheet.com", W / 2, 1006);
  }, [top5, date]);

  useEffect(() => {
    draw();
  }, [draw]);

  const download = () => {
    const a = document.createElement("a");
    a.download = "pooh-sheet-heat-board.png";
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
    toast.success("Heat card downloaded. Post it!");
  };

  const share = async () => {
    canvasRef.current.toBlob(async (blob) => {
      const file = new File([blob], "pooh-sheet-heat-board.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "The Pooh Sheet Heat Board", text: "Today's top 5 launchers on The Pooh Sheet. It's Not A Sin, If You WIN! thepoohsheet.com" });
        } catch (e) { /* cancelled */ }
      } else {
        download();
      }
    }, "image/png");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-heat-modal">
      <div className="card-gold border border-[#a855f7]/40 rounded-xl p-4 md:p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold uppercase">Share the <span className="gold-text">Heat</span></h3>
          <button onClick={onClose} data-testid="share-heat-close" className="text-neutral-500 hover:text-white p-1 transition-colors duration-200"><X size={18} /></button>
        </div>
        <canvas ref={canvasRef} width={W} height={H} className="w-full rounded-lg border border-white/10" />
        <div className="flex gap-2 mt-4">
          <button onClick={share} data-testid="share-heat-share-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase tracking-wider text-xs py-3 rounded transition-colors duration-200">
            <Share2 size={14} /> Share
          </button>
          <button onClick={download} data-testid="share-heat-download-btn"
            className="flex-1 flex items-center justify-center gap-2 border border-white/20 hover:border-[#a855f7] text-white font-bold uppercase tracking-wider text-xs py-3 rounded transition-colors duration-200">
            <Download size={14} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}
